import React from 'react'
import { Link } from 'react-router-dom'
import pluralize from 'pluralize'
import queryString from 'query-string'
import { isEmpty, cloneDeep, isNil } from 'lodash'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { ALL_CHANNEL_TYPES } from '@/utils/constants'

import * as redux from './playlists.redux'

export const fetchPlaylists = (organizationId, includeHistory = true) => {
  const params = queryString.stringify({ requested_organization_id: organizationId })

  return async (dispatch) => {
    dispatch(redux.setLoading({ playlists: true }))

    try {
      if (includeHistory) {
        const [playlistsResponse, playlistsChangesResponse] = await Promise.all([
          fetchingAPI(`${apiService.workflow}/playlists?${params}`, 'GET', dispatch),
          fetchingAPI(`${apiService.workflow}/history?${params}`, 'GET', dispatch),
        ])

        dispatch(redux.setPlaylists(playlistsResponse))
        dispatch(redux.setPlaylistsChanges(playlistsChangesResponse))
      } else {
        const playlistsResponse = await fetchingAPI(
          `${apiService.workflow}/playlists?${params}`,
          'GET',
          dispatch
        )
        dispatch(redux.setPlaylists(playlistsResponse))
      }
    } catch (err) {
      toast.error('Failed to fetch playlists', { cause: err })
    } finally {
      dispatch(redux.setLoading({ playlists: false }))
    }
  }
}

// Distribute the playlist's notes (one bulk fetch) onto each call so the
// table can show a per-call comment count. The note bodies themselves are
// still loaded on demand by the NotesPanel when the drawer opens.
export const mapCommentsToCalls = (calls, comments) => {
  const filteredComments = comments
    .filter((comment) => !comment.deleted)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  return calls.map((call) => {
    const callComments = filteredComments.filter((comment) => comment.call_id === call.call_id)
    return { ...call, callComments }
  })
}

// Re-pull just the playlist's notes (one request) and re-map the counts onto
// the calls already in state, so the table's "View N Comments" badge stays in
// sync after a note is added/edited/deleted from the drawer — without
// reloading the whole playlist. A stale badge is harmless, so failures are
// swallowed rather than surfaced as a toast.
export const refreshPlaylistCommentCounts = () => async (dispatch, getState) => {
  const { playlist } = getState().playlists
  if (isEmpty(playlist?.playlist_calls)) {
    return
  }
  try {
    const comments = await fetchingAPI(
      `${apiService.workflow}/api/notes/playlist/${playlist.uuid}?${queryString.stringify({
        organization_id: playlist.organization_id,
      })}`,
      'GET',
      dispatch
    )
    dispatch(
      redux.setPlaylist({
        ...playlist,
        playlist_calls: mapCommentsToCalls(playlist.playlist_calls, comments),
      })
    )
  } catch (err) {
    // Intentionally silent — a stale count badge isn't worth an error toast.
  }
}

export const fetchPlaylist = (playlistUuid, organizationId) => {
  const params = queryString.stringify({ requested_organization_id: organizationId })

  return async (dispatch) => {
    dispatch(redux.setLoading({ playlist: true }))

    try {
      const [playlistResponse, playlistChangesResponse] = await Promise.all([
        fetchingAPI(`${apiService.workflow}/playlists/${playlistUuid}?${params}`, 'GET', dispatch),
        fetchingAPI(`${apiService.workflow}/history/${playlistUuid}?${params}`, 'GET', dispatch),
      ])

      let calls = []

      if (!isEmpty(playlistResponse.playlist_calls)) {
        const callsResponse = await fetchingAPI(
          `${apiService.reporting}/api/calls?include_scorecards=true`,
          'POST',
          dispatch,
          JSON.stringify({
            call_ids: playlistResponse.playlist_calls.map((value) => value.call_id),
            organization_id: playlistResponse.organization_id,
            call_types: ALL_CHANNEL_TYPES,
          })
        )
        calls = callsResponse.calls
        // One bulk request for every note in the playlist, used only to show
        // the per-call comment count in the table. Note bodies are loaded on
        // demand by the NotesPanel inside the drawer (TREN-273). The count is
        // non-essential, so a failure here degrades to "no counts" rather than
        // failing the whole playlist load.
        try {
          const commentsResponse = await fetchingAPI(
            `${apiService.workflow}/api/notes/playlist/${playlistResponse.uuid}?${queryString.stringify(
              { organization_id: playlistResponse.organization_id }
            )}`,
            'GET',
            dispatch
          )
          calls = mapCommentsToCalls(callsResponse.calls, commentsResponse)
        } catch (err) {
          // Leave `calls` as the un-annotated call list; the count badge just
          // won't render until the next load.
        }
      }

      const playlist = {
        ...playlistResponse,
        playlist_calls: playlistResponse.playlist_calls.map((value) => {
          const callData = calls.find((call) => call.call_id === value.call_id)

          return { ...callData, ...value }
        }),
      }
      // Collect all unique call IDs from playlist changes
      const allCallIdsFromChanges = new Set()
      playlistChangesResponse.forEach((change) => {
        change.calls_added?.forEach((id) => allCallIdsFromChanges.add(id))
        change.calls_removed?.forEach((id) => allCallIdsFromChanges.add(id))
      })

      // Fetch call details for all changed calls
      let updatedChanges = playlistChangesResponse
      if (!isEmpty(allCallIdsFromChanges)) {
        const callsFromChanges = await fetchingAPI(
          `${apiService.reporting}/api/calls`,
          'POST',
          dispatch,
          JSON.stringify({
            call_ids: Array.from(allCallIdsFromChanges),
            organization_id: playlistResponse.organization_id,
            call_types: ALL_CHANNEL_TYPES,
            include_separate_messages: true,
          })
        )

        const getCallKey = (callId) => {
          let callData = callsFromChanges.calls.find((call) => call.call_id === callId)
          if (isNil(callData)) {
            callData = calls.find((call) => {
              return Number(call.call_id) === Number(callId)
            })
          }
          return callData?.message_id ? callData.message_id : callData?.call_id
        }

        // Map playlist changes to use message_id if available
        updatedChanges = playlistChangesResponse.map((change) => ({
          ...change,
          calls_added: change.calls_added?.map(getCallKey),
          calls_removed: change.calls_removed?.map(getCallKey),
          calls_updated: change.calls_updated?.map((callId) => {
            let value = null
            let key = callId
            if (typeof callId === 'object') {
              const [k] = Object.keys(callId)
              value = callId[k]
              key = k
            }
            const callKey = getCallKey(key)
            return value !== null ? { [callKey]: value } : callKey
          }),
        }))
      }

      dispatch(redux.setPlaylist(playlist))
      dispatch(redux.setPlaylistChanges(updatedChanges))
    } catch (err) {
      toast.error('Failed to fetch playlist', { cause: err })
      dispatch(redux.setPlaylist(null))
      dispatch(redux.setPlaylistChanges([]))
    } finally {
      dispatch(redux.setLoading({ playlist: false }))
    }
  }
}

export const createPlaylist = ({
  name,
  organizationId,
  callIds = [],
  visibility = true,
  allowedUsers,
}) => {
  const params = queryString.stringify({ requested_organization_id: organizationId })

  return async (dispatch, getState) => {
    const { currentUser } = getState()
    const body = JSON.stringify({
      name,
      playlist_calls: callIds.map((callId) => ({ call_id: callId })),
      public_visibility: visibility,
      allowed_users: allowedUsers,
    })

    try {
      const response = await fetchingAPI(
        `${apiService.workflow}/playlists?${params}`,
        'POST',
        dispatch,
        body
      )

      await dispatch(fetchPlaylists(organizationId || currentUser.organizationid))

      toast.success(
        <div>
          <p>Playlist created</p>
          <p>
            <Link to={`/playlists/${response.uuid}?organization_id=${organizationId}`}>
              View Playlist
            </Link>
          </p>
        </div>
      )

      return true
    } catch (err) {
      toast.error('Failed to create playlist', { cause: err })

      // Lets the form stay open on failure, so a refused request doesn't
      // throw away what the user typed.
      return false
    }
  }
}

export const patchPlaylist = (
  playlistUuid,
  organizationId,
  values,
  isUserUnsubscribing = false
) => {
  const params = queryString.stringify({ requested_organization_id: organizationId })

  return async (dispatch) => {
    try {
      const body = JSON.stringify(values)

      await fetchingAPI(
        `${apiService.workflow}/playlists/${playlistUuid}?${params}`,
        'PATCH',
        dispatch,
        body
      )
      // Must do your own fetching after this because sometimes it's used on the "playlist" page and sometimes on the "playlists" page

      if (isUserUnsubscribing) {
        toast.success('Successfully unsubscribed from playlist')
      } else {
        toast.success('Playlist updated')
      }
    } catch (err) {
      if (isUserUnsubscribing && err.message === 'Not found.') {
        toast.success('Successfully unsubscribed from playlist')
      } else if (isUserUnsubscribing) {
        toast.error('Failed to unsubscribe from playlist', { cause: err })
      } else {
        toast.error('Failed to update playlist', { cause: err })
      }
    }
  }
}

export const unsubscribeFromPlaylist = (playlist, userId) => {
  return async (dispatch) => {
    const values = { allowed_users: playlist.allowed_users.filter((id) => id !== userId) }
    await dispatch(patchPlaylist(playlist.uuid, playlist.organization_id, values, true))
  }
}

export const addCallsToPlaylist = (playlistUuid, organizationId, callIds = []) => {
  return async (dispatch) => {
    const params = queryString.stringify({ requested_organization_id: organizationId })

    try {
      const body = JSON.stringify({
        playlist_calls: callIds.map((callId) => ({ call_id: callId })),
      })

      await fetchingAPI(
        `${apiService.workflow}/playlists/${playlistUuid}?${params}`,
        'PATCH',
        dispatch,
        body
      )

      toast.success(
        <div>
          <p>{`${pluralize('Call', callIds.length)} added to playlist.`}</p>
          <p>
            <Link
              to={`/playlists/${playlistUuid}?organization_id=${organizationId}`}
              data-testid="toast-playlist-link"
            >
              View Playlist
            </Link>
          </p>
        </div>
      )
    } catch (err) {
      toast.error('Failed to add calls to playlist', { cause: err })
    }
  }
}

export const deletePlaylistCall = (playlistUuid, organizationId, callId) => {
  const params = queryString.stringify({ requested_organization_id: organizationId })

  return async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.workflow}/playlists/${playlistUuid}/playlist_call/${callId}?${params}`,
        'DELETE',
        dispatch
      )
      await dispatch(fetchPlaylist(playlistUuid, organizationId))
      toast.success('Call removed')
    } catch (err) {
      toast.error('Failed to remove call', { cause: err })
    }
  }
}

export const patchPlaylistCall = (
  playlistUuid,
  organizationId,
  callId,
  value,
  manualUpdate = false
) => {
  const params = queryString.stringify({ requested_organization_id: organizationId })

  return async (dispatch) => {
    try {
      const body = JSON.stringify({
        uuid: playlistUuid,
        playlist_calls: [{ call_id: callId, ...value }],
      })

      await fetchingAPI(
        `${apiService.workflow}/playlists/${playlistUuid}?${params}`,
        'PATCH',
        dispatch,
        body
      )

      // Update playlist call manually instead of re-fetching table
      if (manualUpdate) {
        dispatch(redux.setPlaylistCall({ call_id: callId, ...value }))
      } else {
        await dispatch(fetchPlaylist(playlistUuid, organizationId))
      }

      toast.success('Call updated')
    } catch (err) {
      toast.error('Failed to update call', { cause: err })
    }
  }
}

export const deletePlaylist = (playlistUuid, organizationId) => {
  const params = queryString.stringify({ requested_organization_id: organizationId })

  return async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.workflow}/playlists/${playlistUuid}?${params}`,
        'DELETE',
        dispatch
      )
      await dispatch(fetchPlaylists(organizationId))
      toast.success('Playlist deleted')
    } catch (err) {
      toast.error('Failed to delete playlist', { cause: err })
    }
  }
}

export const fetchPlaylistsByCallId = (callId) => {
  return async (dispatch) => {
    try {
      dispatch(redux.setLoading({ playlistsByCallId: true }))

      const response = await fetchingAPI(
        `${apiService.workflow}/playlists/by_call_id/${callId}`,
        'GET',
        dispatch
      )

      dispatch(redux.setPlaylistsByCallId(response))
    } catch (err) {
      toast.error('Failed to fetch associated playlists', { cause: err })
    } finally {
      dispatch(redux.setLoading({ playlistsByCallId: false }))
    }
  }
}
export const fetchPlaylistsByMessageId = (messageId) => {
  return async (dispatch) => {
    try {
      dispatch(redux.setLoading({ playlistsByCallId: true }))

      const response = await fetchingAPI(
        `${apiService.workflow}/playlists/by_message_id/${messageId}`,
        'GET',
        dispatch
      )

      dispatch(redux.setPlaylistsByCallId(response))
    } catch (err) {
      toast.error('Failed to fetch associated playlists', { cause: err })
    } finally {
      dispatch(redux.setLoading({ playlistsByCallId: false }))
    }
  }
}

// Duplicated from call explorer
export const fetchSummariesForCallIds = (call_ids) => async (dispatch, getState) => {
  const validCallIds = call_ids.filter((id) => id != null)

  if (validCallIds.length === 0) {
    return
  }

  const { playlist } = getState().playlists
  const playlistCalls = playlist?.playlist_calls || []
  const callsCopy = cloneDeep(playlistCalls).map((call) => {
    if (validCallIds.includes(call.call_id)) {
      return {
        ...call,
        // this empty object is tracking which call_ids we make requests for.
        // If call_summary is empty object we requested and got nothing back so
        // no summary otherwise request if this call is rendered and call_summary is undefined
        call_summary: {},
      }
    }
    return call
  })

  try {
    const summaries = await fetchingAPI(
      `${apiService.summary}/summaries/multiple`,
      'POST',
      dispatch,
      JSON.stringify({ call_ids: validCallIds })
    )
    callsCopy.forEach((call) => {
      if (call.call_summary) {
        const summaryForCall = summaries.find((summary) => summary.call_id === call.call_id)
        if (summaryForCall?.parsed_summary) {
          Object.assign(call, { call_summary: summaryForCall.parsed_summary })
          Object.assign(call, { ...summaryForCall.parsed_summary })
        }
      }
    })
    dispatch(redux.setPlaylist({ ...playlist, playlist_calls: callsCopy }))
  } catch (err) {
    if (err?.status !== 404) {
      toast.error(err?.message || 'Failed to fetch summaries')
    }
  }
}
